import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useRef, useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Plus, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle } from "lucide-react";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { toast } from "sonner";
import { CityZipInput } from "@/components/forms/CityZipInput";
import { SpecialtySelect } from "@/components/forms/SpecialtySelect";
import { AddressAutocomplete } from "@/components/forms/AddressAutocomplete";
import { MultiTagSelect } from "@/components/forms/MultiTagSelect";
import { SERVICES, LANGUAGES, INSURANCES } from "@/data/heallexa";
import { useServerFn } from "@tanstack/react-start";
import { bulkImportStart, bulkImportChunk, bulkImportFinish } from "@/server/bulk-import.functions";
import { createListingFn } from "./-dashboard.admin.add-listings.functions";

export const Route = createFileRoute("/dashboard/admin/add-listings")({
 validateSearch: (s: Record<string, unknown>) => ({
 tab: s.tab === "bulk" ? "bulk" : "single",
 }),
 component: AddListingsPage,
});

type Row = Record<string, any>;

// --- 18-column basic-listing CSV schema ---
// Internal-only (never rendered publicly): NPI, License #, License State, Sex, Fax, Zip, Email
const BULK_HEADERS = [
 "practice_name", "first_name", "middle_name", "last_name", "provider_sex",
 "address", "city", "state", "zip_code", "phone", "fax",
 "license_number", "license_state", "credentials", "specialty",
 "email", "website", "npi",
] as const;

const HEADER_ALIAS: Record<string, (typeof BULK_HEADERS)[number]> = {
 practice: "practice_name", practice_name: "practice_name",
 first_name: "first_name", first: "first_name",
 middle_name: "middle_name", middle: "middle_name",
 last_name: "last_name", last: "last_name",
 provider_sex: "provider_sex", sex: "provider_sex", gender: "provider_sex",
 address: "address",
 city: "city",
 state: "state",
 zip: "zip_code", zip_code: "zip_code", zipcode: "zip_code", postal_code: "zip_code",
 phone: "phone",
 fax: "fax",
 license_number: "license_number", license: "license_number", license_no: "license_number",
 license_state: "license_state",
 credentials: "credentials",
 specialty: "specialty",
 email: "email",
 website: "website", url: "website",
 npi: "npi",
};

const TEMPLATE_CSV = `Practice Name,First Name,Middle Name,Last Name,Provider Sex,Address,City,State,Zip Code,Phone,Fax,License Number,License State,Credentials,Specialty,Email,Website,NPI
Smith Family Dental,Jane,A,Smith,F,123 Main St,Austin,TX,78701,(555) 123-4567,(555) 123-4568,DDS-12345,TX,DDS,Dentist,jane@example.com,https://example.com,1234567890
`;

function normHeader(h: string): string {
 return h.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

function AddListingsPage() {
 const { user, loading, primaryRole } = useAuth();
 const navigate = useNavigate();
 const search = Route.useSearch();
 const [tab, setTab] = useState<"single" | "bulk">(search.tab === "bulk" ? "bulk" : "single");

 useEffect(() => {
 if (loading) return;
 if (!user) { navigate({ to: "/login" }); return; }
 if (primaryRole && primaryRole !== "admin") { navigate({ to: "/dashboard" }); return; }
 }, [user, loading, primaryRole]);

 if (loading || !user || primaryRole !== "admin") {
 return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
 }

  return (
 <AdminPageShell
   title="Add Listings"
   description="Create one listing manually, or stream-import any size CSV."
 >
 <div className="mb-6 flex gap-1 border-b border-border">
 <TabBtn active={tab === "single"} onClick={() => setTab("single")}>Single listing</TabBtn>
 <TabBtn active={tab === "bulk"} onClick={() => setTab("bulk")}>Bulk import (CSV)</TabBtn>
 </div>
 {tab === "single" ? <SingleForm /> : <BulkImport />}
 </AdminPageShell>
 );
}

function TabBtn({ active, onClick, children }: any) {
 return <button onClick={onClick} className={`px-4 py-2.5 text-sm font-medium border-b-2 ${active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{children}</button>;
}

// ============================================================
// Single listing form
// ============================================================
const ARRAY_COLS = new Set(["services", "languages", "insurance", "photos", "available_days"]);
const SINGLE_REQUIRED = ["practice", "phone", "address", "city", "specialty"] as const;

function SingleForm() {
 const [form, setForm] = useState<Row>({});
 const [busy, setBusy] = useState(false);
 const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

 const submit = async () => {
 for (const k of SINGLE_REQUIRED) {
 if (!form[k] || String(form[k]).trim() === "") {
 toast.error(`Missing "${k}"`);
 return;
 }
 }
 setBusy(true);
 const payload: Row = { ...form };
 for (const k of ARRAY_COLS) {
 if (typeof payload[k] === "string") payload[k] = payload[k].split(/[|;,]/).map((s: string) => s.trim()).filter(Boolean);
 }
 // Compose name from first/last/credentials if name not provided
 if (!payload.name || !String(payload.name).trim()) {
 const parts = [payload.first_name, payload.middle_name, payload.last_name].map((s: any) => (s ?? "").trim()).filter(Boolean);
 const creds = (payload.credentials ?? "").trim();
 payload.name = parts.length ? (creds ? `${parts.join(" ")}, ${creds}` : parts.join(" ")) : payload.practice;
 }
 // Map sex → gender
 if (payload.provider_sex) {
 const s = String(payload.provider_sex).trim().toLowerCase();
 payload.gender = s.startsWith("f") ? "female" : s.startsWith("m") ? "male" : null;
 }
 try {
 const data = await createListingFn({ data: { payload } });
 toast.success("Listing created");
 if (data?.id && payload.email) {
 const { triggerListingEmailSequence } = await import("@/lib/listing-email-sequence");
 await triggerListingEmailSequence(data.id, { adminCreated: true, ownerEmail: payload.email });
 }
 setForm({});
 } catch (e: any) {
 toast.error(e?.message ?? "Failed to create listing");
 } finally {
 setBusy(false);
 }
 };

 return (
 <div className="border border-border bg-card p-6 space-y-6">
 {/* Public-facing fields */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <Field label="Practice name *" value={form.practice} onChange={(v) => set("practice", v)} />
 <div>
 <Label className="text-xs">Specialty *</Label>
 <div className="mt-1"><SpecialtySelect value={form.specialty ?? ""} onChange={(v) => set("specialty", v)} /></div>
 </div>
 <Field label="Phone *" value={form.phone} onChange={(v) => set("phone", v)} />
 <Field label="Website" value={form.website} onChange={(v) => set("website", v)} />
 <Field label="Logo URL" value={form.logo_url} onChange={(v) => set("logo_url", v)} />
 <Field label="Hours" value={form.hours} onChange={(v) => set("hours", v)} placeholder="Mon Fri 9 5" />
 </div>

 <AddressAutocomplete
 address={form.address ?? ""}
 onPick={({ address, city, state, zip }) =>
 setForm((f) => ({ ...f, address, city: city ?? f.city, state: state ?? f.state, zip: zip ?? f.zip }))
 }
 />
 <CityZipInput
 required
 city={form.city ?? ""}
 state={form.state ?? ""}
 zip={form.zip ?? ""}
 onChange={({ city, state, zip }) => setForm((f) => ({ ...f, city, state, zip }))}
 />

 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div>
 <Label className="text-xs">Services</Label>
 <div className="mt-1"><MultiTagSelect value={Array.isArray(form.services) ? form.services : []} onChange={(v) => set("services", v)} options={SERVICES} placeholder="Select services…" /></div>
 </div>
 <div>
 <Label className="text-xs">Languages</Label>
 <div className="mt-1"><MultiTagSelect value={Array.isArray(form.languages) ? form.languages : []} onChange={(v) => set("languages", v)} options={LANGUAGES} placeholder="Select languages…" /></div>
 </div>
 <div>
 <Label className="text-xs">Insurance</Label>
 <div className="mt-1"><MultiTagSelect value={Array.isArray(form.insurance) ? form.insurance : []} onChange={(v) => set("insurance", v)} options={INSURANCES.map((i) => i.id)} placeholder="Select insurance…" /></div>
 </div>
 </div>
 <div>
 <Label className="text-xs">About</Label>
 <Textarea rows={4} value={form.about ?? ""} onChange={(e) => set("about", e.target.value)} className="mt-1" />
 </div>

 {/* Provider name + credentials */}
 <div className="border-t border-border pt-6">
 <h3 className="text-sm font-semibold mb-3">Provider name (optional)</h3>
 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
 <Field label="First name" value={form.first_name} onChange={(v) => set("first_name", v)} />
 <Field label="Middle name" value={form.middle_name} onChange={(v) => set("middle_name", v)} />
 <Field label="Last name" value={form.last_name} onChange={(v) => set("last_name", v)} />
 <Field label="Credentials" value={form.credentials} onChange={(v) => set("credentials", v)} placeholder="e.g. MD, DDS" />
 </div>
 </div>

 {/* Internal-only identifiers, never displayed publicly */}
 <div className="border-t border-border pt-6 bg-muted/30 -mx-6 -mb-6 px-6 pb-6 mt-6">
 <div className="flex items-center gap-2 mb-3">
 <h3 className="text-sm font-semibold">Internal identifiers</h3>
 <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-background border border-border px-2 py-0.5">Internal only</span>
 </div>
 <p className="text-xs text-muted-foreground mb-4">These fields are never shown on public listing pages, used for dedup and admin records.</p>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <Field label="Email" value={form.email} onChange={(v) => set("email", v)} type="email" />
 <Field label="NPI" value={form.npi} onChange={(v) => set("npi", v)} placeholder="10-digit NPI" />
 <div>
 <Label className="text-xs">Provider sex</Label>
 <select
 className="mt-1 w-full h-9 px-3 text-sm border border-border bg-background"
 value={form.provider_sex ?? ""}
 onChange={(e) => set("provider_sex", e.target.value)}
 >
 <option value=""> </option>
 <option value="F">Female</option>
 <option value="M">Male</option>
 </select>
 </div>
 <Field label="License #" value={form.license_number} onChange={(v) => set("license_number", v)} />
 <Field label="License state" value={form.license_state} onChange={(v) => set("license_state", v)} placeholder="e.g. TX" />
 <Field label="Fax" value={form.fax} onChange={(v) => set("fax", v)} />
 </div>
 </div>

 <div className="flex justify-end">
 <Button onClick={submit} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create listing</Button>
 </div>
 </div>
 );
}

function Field({ label, value, onChange, placeholder, type }: { label: string; value: any; onChange: (v: string) => void; placeholder?: string; type?: string }) {
 return (
 <div>
 <Label className="text-xs">{label}</Label>
 <Input type={type ?? "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1" />
 </div>
 );
}

// ============================================================
// Bulk import, streaming CSV parser, server-side ingest
// ============================================================
const CHUNK_SIZE = 1000;

type ParsedRow = Record<string, string>;

/** Streaming CSV parser. Calls onRow for each data row, onDone at EOF. Handles RFC 4180 quoted fields. */
async function streamCsv(
 file: File,
 onRow: (row: ParsedRow, rowNumber: number) => Promise<void> | void,
 onProgress: (bytesRead: number, totalBytes: number) => void,
) {
 const stream = file.stream().pipeThrough(new TextDecoderStream("utf-8"));
 const reader = stream.getReader();
 let buf = "";
 let inQuotes = false;
 let cur = "";
 let row: string[] = [];
 let headers: string[] | null = null;
 let rowNum = 0;
 let bytesRead = 0;
 const total = file.size;

 const flushRow = async () => {
 row.push(cur); cur = "";
 if (!headers) {
 headers = row.map(normHeader).map((h) => HEADER_ALIAS[h] ?? h);
 row = [];
 return;
 }
 rowNum++;
 if (row.every((v) => !v || !v.trim())) { row = []; return; } // skip blank rows
 const obj: ParsedRow = {};
 headers.forEach((h, i) => { if (h) obj[h] = (row[i] ?? "").trim(); });
 row = [];
 await onRow(obj, rowNum + 1); // +1 because header is line 1
 };

 while (true) {
 const { value, done } = await reader.read();
 if (value) {
 buf += value;
 bytesRead += value.length;
 onProgress(Math.min(bytesRead, total), total);
 // Process character by character, only the buffer we just appended
 for (let i = 0; i < buf.length; i++) {
 const c = buf[i];
 if (inQuotes) {
 if (c === '"') {
 if (buf[i + 1] === '"') { cur += '"'; i++; } else { inQuotes = false; }
 } else cur += c;
 } else {
 if (c === '"') inQuotes = true;
 else if (c === ",") { row.push(cur); cur = ""; }
 else if (c === "\n" || c === "\r") {
 if (c === "\r" && buf[i + 1] === "\n") i++;
 await flushRow();
 } else cur += c;
 }
 }
 buf = "";
 }
 if (done) {
 // Flush trailing field/row if not terminated by newline
 if (cur.length || row.length) {
 await flushRow();
 }
 break;
 }
 }
}

type Progress = {
 phase: "idle" | "uploading" | "processing" | "done" | "error";
 totalBytes: number;
 bytesRead: number;
 rowsParsed: number;
 rowsInserted: number;
 rowsFailed: number;
 batchId: string | null;
 errors: { row: number; error: string }[];
 message?: string;
};

function BulkImport() {
 const fileRef = useRef<HTMLInputElement>(null);
 const [file, setFile] = useState<File | null>(null);
 const [progress, setProgress] = useState<Progress>({
 phase: "idle", totalBytes: 0, bytesRead: 0, rowsParsed: 0, rowsInserted: 0, rowsFailed: 0, batchId: null, errors: [],
 });

 const startFn = useServerFn(bulkImportStart);
 const chunkFn = useServerFn(bulkImportChunk);
 const finishFn = useServerFn(bulkImportFinish);

 const downloadTemplate = () => {
 const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
 const url = URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url; a.download = "heallexa-listings-template.csv"; a.click();
 URL.revokeObjectURL(url);
 };

 const runImport = async () => {
 if (!file) { toast.error("Choose a CSV file first"); return; }
 setProgress({
 phase: "processing", totalBytes: file.size, bytesRead: 0, rowsParsed: 0, rowsInserted: 0, rowsFailed: 0, batchId: null, errors: [],
 });

 let batchId: string | null = null;
 try {
 const start = await startFn({ data: { total_rows: 0, notes: `${file.name} (${(file.size / 1_048_576).toFixed(1)} MB)` } });
 batchId = start.batch_id;
 setProgress((p) => ({ ...p, batchId }));

 let pending: ParsedRow[] = [];
 let parsed = 0;

 const flush = async () => {
 if (!pending.length) return;
 const slice = pending; pending = [];
 try {
 const res = await chunkFn({ data: { batch_id: batchId!, rows: slice as any } });
 setProgress((p) => ({
 ...p,
 rowsInserted: p.rowsInserted + res.inserted,
 rowsFailed: p.rowsFailed + res.failed,
 errors: res.errors.length ? [...p.errors, ...res.errors.map((e) => ({ row: e.index, error: e.error }))].slice(0, 100) : p.errors,
 }));
 } catch (e: any) {
 setProgress((p) => ({
 ...p,
 rowsFailed: p.rowsFailed + slice.length,
 errors: [...p.errors, { row: parsed, error: e?.message ?? "chunk failed" }].slice(0, 100),
 }));
 }
 };

 await streamCsv(file,
 async (row, rowNumber) => {
 parsed++;
 // Skip rows with no practice name
 if (!row.practice_name) {
 setProgress((p) => ({ ...p, rowsParsed: parsed, rowsFailed: p.rowsFailed + 1, errors: p.errors.length < 100 ? [...p.errors, { row: rowNumber, error: "Missing practice name" }] : p.errors }));
 return;
 }
 pending.push(row);
 if (pending.length >= CHUNK_SIZE) {
 await flush();
 setProgress((p) => ({ ...p, rowsParsed: parsed }));
 }
 },
 (bytesRead, totalBytes) => {
 setProgress((p) => ({ ...p, bytesRead, totalBytes }));
 },
 );
 await flush();

 await finishFn({ data: { batch_id: batchId } });
 setProgress((p) => ({ ...p, phase: "done", rowsParsed: parsed }));
 toast.success(`Imported ${parsed} rows`);
 } catch (e: any) {
 console.error(e);
 if (batchId) { try { await finishFn({ data: { batch_id: batchId } }); } catch {} }
 setProgress((p) => ({ ...p, phase: "error", message: e?.message ?? "Import failed" }));
 toast.error(e?.message ?? "Import failed");
 }
 };

 const pct = progress.totalBytes ? Math.round((progress.bytesRead / progress.totalBytes) * 100) : 0;
 const busy = progress.phase === "processing" || progress.phase === "uploading";

 return (
 <div className="space-y-6">
 <div className="border border-border bg-card p-6 space-y-4">
 <div className="flex items-center justify-between flex-wrap gap-3">
 <div>
 <h2 className="font-semibold flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" /> Upload CSV</h2>
 <p className="text-xs text-muted-foreground mt-1 max-w-xl">
 18 columns: Practice Name, First Name, Middle Name, Last Name, Provider Sex, Address, City, State, Zip Code, Phone, Fax, License Number, License State, Credentials, Specialty, Email, Website, NPI. Files of any size are streamed and processed in {CHUNK_SIZE}-row chunks. Claim emails and notifications are suppressed during bulk import.
 </p>
 </div>
 <div className="flex gap-2">
 <Button variant="outline" size="sm" onClick={downloadTemplate}><Download className="h-4 w-4" /> Template</Button>
 <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}><Upload className="h-4 w-4" /> Choose file</Button>
 <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setProgress({ phase: "idle", totalBytes: f.size, bytesRead: 0, rowsParsed: 0, rowsInserted: 0, rowsFailed: 0, batchId: null, errors: [] }); } }} />
 </div>
 </div>

 {file && (
 <div className="text-sm border border-border bg-background px-3 py-2 flex items-center justify-between gap-3">
 <span className="truncate"><FileSpreadsheet className="h-4 w-4 inline mr-2 text-muted-foreground" />{file.name} <span className="text-xs text-muted-foreground">({(file.size / 1_048_576).toFixed(2)} MB)</span></span>
 <Button size="sm" onClick={runImport} disabled={busy}>
 {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import now
 </Button>
 </div>
 )}

 {progress.phase !== "idle" && (
 <div className="space-y-3">
 <div>
 <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
 <span>{progress.phase === "done" ? "Complete" : progress.phase === "error" ? "Error" : "Processing"}</span>
 <span>{pct}% · {(progress.bytesRead / 1_048_576).toFixed(1)} / {(progress.totalBytes / 1_048_576).toFixed(1)} MB</span>
 </div>
 <div className="h-2 bg-muted rounded overflow-hidden">
 <div className={`h-full transition-all ${progress.phase === "error" ? "bg-destructive" : "bg-primary"}`} style={{ width: `${pct}%` }} />
 </div>
 </div>
 <div className="grid grid-cols-3 gap-3 text-sm">
 <Stat label="Parsed" value={progress.rowsParsed} />
 <Stat label="Inserted" value={progress.rowsInserted} tone="primary" />
 <Stat label="Failed" value={progress.rowsFailed} tone={progress.rowsFailed ? "destructive" : "muted"} />
 </div>
 {progress.batchId && (
 <p className="text-xs text-muted-foreground">
 Batch <code className="font-mono">{progress.batchId.slice(0, 8)}</code>, view in{" "}
 <Link to="/dashboard/admin/bulk-imports" className="underline">Bulk imports audit</Link>
 </p>
 )}
 {progress.message && (
 <div className="text-xs text-destructive border border-destructive/40 bg-destructive/5 px-3 py-2 flex items-start gap-2">
 <AlertCircle className="h-3.5 w-3.5 mt-0.5" /> {progress.message}
 </div>
 )}
 </div>
 )}
 </div>

 {progress.errors.length > 0 && (
 <div className="border border-border bg-card p-4 space-y-3">
 <h3 className="font-semibold text-sm flex items-center gap-2"><AlertCircle className="h-4 w-4 text-destructive" /> First {progress.errors.length} errors</h3>
 <div className="overflow-x-auto border border-border max-h-72 overflow-y-auto">
 <table className="w-full text-xs">
 <thead className="bg-muted/40 text-muted-foreground"><tr><th className="px-2 py-1.5 text-left">Row</th><th className="px-2 py-1.5 text-left">Error</th></tr></thead>
 <tbody>{progress.errors.map((f, i) => <tr key={i} className="border-t border-border"><td className="px-2 py-1.5">{f.row}</td><td className="px-2 py-1.5 text-destructive">{f.error}</td></tr>)}</tbody>
 </table>
 </div>
 </div>
 )}

 {progress.phase === "done" && (
 <div className="border border-border bg-card p-4 flex items-start gap-3">
 <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
 <div>
 <p className="font-semibold text-sm">Import complete</p>
 <p className="text-xs text-muted-foreground mt-1">
 {progress.rowsInserted} listings inserted, {progress.rowsFailed} skipped. No claim emails were sent, providers will receive them when they claim their listing.
 </p>
 </div>
 </div>
 )}
 </div>
 );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "primary" | "destructive" | "muted" }) {
 const color = tone === "primary" ? "text-primary" : tone === "destructive" ? "text-destructive" : "text-foreground";
 return (
 <div className="border border-border px-3 py-2">
 <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
 <div className={`text-lg font-semibold ${color}`}>{value.toLocaleString()}</div>
 </div>
 );
}
